import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectAuthIsAdmin, logoutUser } from '../slices/authSlice';
import { selectSeenVersion, markVersionSeen, selectSidebarCollapsed, setSidebarCollapsed } from '../slices/preferencesSlice';
import ThemedLogo from './ThemedLogo';
import SidebarLogo from './SidebarLogo';
import { selectAuthIsForecaster } from "../slices/authSlice";
import { ChevronDoubleLeftIcon, ChevronRightIcon, Cog6ToothIcon, XMarkIcon, PowerIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { NavLink, useLocation } from 'react-router-dom';
import pkg from '../../package.json';
import { selectAuthIsMarketOwner, selectAuthIsMarketOwnerReadOnly } from '../slices/authSlice';
import PermissionChecker from '../utils/permissionChecker';
import { isPathActive } from '../utils/isPathActive';
import ConfirmDialog from './ConfirmDialog';
import { SLIDE_OVER_TOP_OFFSET_STYLE } from './slideOverTransition';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Sidebar({ navigation, sidebarOpen, setSidebarOpen }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const isAdmin = useSelector(selectAuthIsAdmin); // Get admin status
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const isForecaster = useSelector(selectAuthIsForecaster);
  const seenVersion = useSelector(selectSeenVersion);
  const hasNewRelease = pkg?.version && seenVersion !== pkg.version;
  const collapsed = useSelector(selectSidebarCollapsed);
  const handleToggle = () => dispatch(setSidebarCollapsed(!collapsed));

  const handleDismissNewRelease = () => {
    dispatch(markVersionSeen(pkg.version));
  };

  // Central permission checker instance for the current user
  const permissionChecker = new PermissionChecker({
    isAdmin,
    isMarketOwner,
    isMarketOwnerReadOnly,
    isForecaster,
  });

  // Logout handlers
  const handleLogoutClick = (e) => {
    e.preventDefault();
    setLogoutConfirmOpen(true);
  };

  const handleLogoutConfirm = () => {
    dispatch(logoutUser());
    setLogoutConfirmOpen(false);
    navigate('/', { replace: true });
  };

  const handleLogoutCancel = () => {
    setLogoutConfirmOpen(false);
  };

  // Use permissionChecker.allows(item) to decide visibility
  const hasPermission = (item) => {
    if (item?.forecasterOnly && !isForecaster) return false;
    return permissionChecker.allows(item);
  };

  // Filter out items based on permissions, then flatten single-child parents
  // for non-admins when a `flattenLabel` is provided on the parent.
  const filteredNavigation = navigation
    .map(item => ({
      ...item,
      children: item.children ? item.children.filter(hasPermission) : null
    }))
    .filter(hasPermission) // Remove parent items that the user shouldn't see
    .map(item => {
      if (!isAdmin && item.flattenLabel && item.children && item.children.length === 1) {
        const [child] = item.children;
        return { ...item, name: item.flattenLabel, href: child.href, children: null };
      }
      return item;
    });

  // The phone drawer drops items flagged hideOnMobile (e.g. Files — a
  // desktop workflow); the desktop rail keeps the full list.
  const mobileNavigation = filteredNavigation.filter((item) => !item.hideOnMobile);

  return (
    <>
      {/* Mobile sidebar */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <div className="fixed inset-0 bg-black/70" />
        <div className="fixed inset-0 flex" style={SLIDE_OVER_TOP_OFFSET_STYLE}>
          <DialogPanel className="relative mr-16 flex w-full max-w-[280px] flex-1 bg-surface dark:bg-gray-870">
            <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
              <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
              </button>
            </div>
            <div className="relative flex flex-col gap-y-5 overflow-y-auto p-4 sm:p-6 pb-4 h-full w-full">
              <div className="flex h-14 sm:h-16 items-center mb-2 sm:mb-4">
                <ThemedLogo alt="Predico" className="h-10 w-auto sm:h-14" />
              </div>
              <NavigationList
              navigation={mobileNavigation}
              logoutConfirmOpen={logoutConfirmOpen}
              handleLogoutClick={handleLogoutClick}
              handleLogoutConfirm={handleLogoutConfirm}
              handleLogoutCancel={handleLogoutCancel}
            />
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop sidebar */}
      <div
        style={{ top: 'var(--playground-banner-height, 0px)' }}
        className={`hidden lg:fixed lg:z-50 lg:inset-y-0 lg:flex lg:flex-col bg-surface dark:bg-gray-870 lg:shadow-[6px_0_16px_-10px_rgba(16,24,40,0.15)] dark:lg:shadow-[6px_0_20px_-10px_rgba(0,0,0,0.6)] relative overflow-hidden transition-all duration-300 ${collapsed ? 'lg:w-16' : 'lg:w-[224px]'}`}
      >
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <div className={`pt-6 pb-4 transition-all duration-300 ${collapsed ? 'px-2' : 'px-4'}`}>
            {/* The button and the wordmark are positioned against the scroll
                container, not this row, so the row's padding change does not
                move them: the wordmark's p stays put and becomes the
                collapsed mark (SidebarLogo). */}
            <div className="flex h-16 items-center justify-end mb-4">
              {collapsed ? (
                <button
                  onClick={handleToggle}
                  title="Expand sidebar"
                  aria-label="Expand sidebar"
                  className="peer absolute left-2 top-6 z-10 h-16 w-12 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                />
              ) : (
                <button
                  onClick={handleToggle}
                  title="Collapse sidebar"
                  className="flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-gray-100/60 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700/50 dark:hover:text-gray-300 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  aria-label="Collapse sidebar"
                >
                  <ChevronDoubleLeftIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                </button>
              )}
              <SidebarLogo
                collapsed={collapsed}
                className="pointer-events-none absolute left-4 top-7 h-14 w-auto transition-opacity peer-hover:opacity-80"
              />
            </div>
            <NavigationList
              navigation={filteredNavigation}
              hasNewRelease={hasNewRelease}
              onNewReleaseSeen={handleDismissNewRelease}
              logoutConfirmOpen={logoutConfirmOpen}
              handleLogoutClick={handleLogoutClick}
              handleLogoutConfirm={handleLogoutConfirm}
              handleLogoutCancel={handleLogoutCancel}
              collapsed={collapsed}
            />
          </div>

          {/* App info footer - Fixed at bottom */}
          {collapsed && (
            <div className="mt-auto pb-6 pt-4 border-t border-gray-200/70 dark:border-gray-600/70 flex justify-center items-start">
              <span className="text-[9px] font-medium text-gray-400 dark:text-gray-500 leading-none tracking-tight select-none">
                v{pkg?.version}
              </span>
            </div>
          )}
          {!collapsed && (
            <div className="mt-auto p-4 pt-4 border-t border-gray-200/70 dark:border-gray-600/70">

              {/* New release pill */}
              {hasNewRelease && (
                <div className="mb-3">
                  <NavLink
                    to="/changelog"
                    onClick={handleDismissNewRelease}
                    className="group relative flex items-center gap-x-2 rounded-md px-3 py-2 pr-7
                               bg-brand-50 hover:bg-brand-100
                               dark:bg-brand-950/40 dark:hover:bg-brand-900/50
                               border border-brand-200 dark:border-brand-800
                               transition-colors duration-150
                               focus-visible:outline-none focus-visible:ring-2
                               focus-visible:ring-brand-500 focus-visible:ring-offset-2
                               dark:focus-visible:ring-offset-gray-800"
                  >
                    <SparklesIcon className="h-3.5 w-3.5 flex-shrink-0 text-brand-500 dark:text-brand-400" aria-hidden="true" />
                    <span className="flex flex-col text-xs font-medium text-brand-700 dark:text-brand-300">
                      <span>New release v{pkg.version}</span>
                      <span className="font-normal text-brand-500 dark:text-brand-400">see more</span>
                    </span>
                    <XMarkIcon
                      className="absolute right-2 top-1/2 -translate-y-1/2
                                 h-3.5 w-3.5 text-brand-400 dark:text-brand-500
                                 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      aria-hidden="true"
                      onClick={(e) => { e.preventDefault(); handleDismissNewRelease(); }}
                    />
                  </NavLink>
                </div>
              )}

              {/* Version row — px-3 matches the nav items' inset so it aligns
                  with the icon/label column (left) and chevron column (right). */}
              <div className="px-3 text-xs text-gray-500 dark:text-gray-400 leading-5">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-300">Version</span>
                  <span className="font-medium text-gray-600 dark:text-gray-200">{pkg?.version || 'N/A'}</span>
                </div>
                <div className="mt-1 truncate">
                  {/* Additional user info can go here */}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

/**
 * Flyout popup rendered via a React portal so it escapes any overflow/clip
 * ancestor on the sidebar. Position is computed fresh on every render and
 * clamped so the panel never goes off-screen.
 */
function FlyoutMenu({ triggerEl, item, onMouseEnter, onMouseLeave, onClose }) {
  const panelRef = useRef(null);
  const [style, setStyle] = useState({ visibility: 'hidden', top: 0 });

  // Recompute position whenever the trigger element changes.
  useLayoutEffect(() => {
    if (!triggerEl || !panelRef.current) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const panelHeight = panelRef.current.offsetHeight;
    const MARGIN = 8; // px away from viewport edges
    // Sidebar rail is w-16 = 64px; place panel flush against it.
    const left = 64;
    // Align top of panel with top of the trigger icon row.
    let top = triggerRect.top;
    // Clamp so the panel doesn't overflow below the viewport.
    const maxTop = window.innerHeight - panelHeight - MARGIN;
    if (top > maxTop) top = Math.max(MARGIN, maxTop);

    setStyle(prev => {
      if (prev.visibility === 'visible' && prev.top === top && prev.left === left) return prev;
      return { visibility: 'visible', top, left };
    });
  }, [triggerEl]);

  return ReactDOM.createPortal(
    <div
      ref={panelRef}
      style={style}
      className="fixed z-[200] min-w-[180px] rounded-lg bg-white dark:bg-gray-870 shadow-xl border border-gray-200/70 dark:border-gray-600/70 py-1.5 overflow-hidden"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {item.children.map((child) => (
        child.external ? (
          <a
            key={child.name}
            href={child.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className={classNames(
              'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-gray-100',
              'flex items-center gap-x-2.5 px-3.5 py-2 text-sm transition-colors duration-100'
            )}
          >
            <span className="block w-1.5 h-1.5 rounded-full flex-shrink-0 bg-gray-300 dark:bg-gray-600" />
            {child.name}
          </a>
        ) : (
          <NavLink
            key={child.name}
            to={child.href}
            end
            onClick={onClose}
            className={({ isActive }) =>
              classNames(
                isActive
                  ? 'bg-brand-50 text-brand-900 dark:bg-brand-900/20 dark:text-brand-300 font-medium'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-gray-100',
                'flex items-center gap-x-2.5 px-3.5 py-2 text-sm transition-colors duration-100'
              )
            }
          >
            {({ isActive }) => (
              <>
                <span className={classNames(
                  'block w-1.5 h-1.5 rounded-full flex-shrink-0',
                  isActive ? 'bg-brand-500' : 'bg-gray-300 dark:bg-gray-600'
                )} />
                {child.name}
              </>
            )}
          </NavLink>
        )
      ))}
    </div>,
    document.body
  );
}

function NavigationList({ navigation, hasNewRelease, onNewReleaseSeen, logoutConfirmOpen, handleLogoutClick, handleLogoutConfirm, handleLogoutCancel, collapsed }) {
  const location = useLocation();
  // flyout stores { item, triggerEl } — triggerEl is the live DOM node so
  // FlyoutMenu can measure its position fresh on every render.
  const [flyout, setFlyout] = useState(null);
  const closeTimerRef = useRef(null);

  // Clear any pending close timer when the component unmounts.
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const openFlyout = (e, item) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setFlyout({ item, triggerEl: e.currentTarget });
  };

  const scheduleFlyoutClose = () => {
    closeTimerRef.current = setTimeout(() => setFlyout(null), 120);
  };

  const cancelFlyoutClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  // Shared class for nav item rows
  const navItemClass = collapsed
    ? 'group flex w-full items-center justify-center rounded-md px-0 py-3 sm:py-2.5 text-sm font-medium leading-6 tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'
    : 'group flex items-center justify-between gap-x-3 rounded-md px-3 py-3 sm:py-2.5 text-sm font-medium leading-6 tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900';

  return (
    <nav className="flex-1">
      <ul className="flex flex-col gap-y-7">
        <li>
          <ul className="space-y-0.5">
            {navigation.map((item) => {
              const isExternal = item.external;
              const isActiveParent = item.children && item.children.some((subItem) => isPathActive(location.pathname, subItem.href));
              const isAnySubItemActive = item.children && isPathActive(location.pathname, item.href);

              return (
                <li key={item.name}>
                  {!item.children ? (
                    isExternal ? (
                      // External link
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={collapsed ? item.name : undefined}
                        className={classNames(
                          'text-gray-700 hover:bg-gray-100/60 dark:text-gray-300 dark:hover:bg-gray-700/50',
                          navItemClass
                        )}
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0 text-gray-500 group-hover:text-brand-700 dark:text-gray-400 dark:group-hover:text-brand-400 transition-colors" />
                        {!collapsed && <span className="truncate flex-1">{item.name}</span>}
                        {collapsed && <span className="sr-only">{item.name}</span>}
                      </a>
                    ) : (
                      // Internal link
                      <NavLink
                        to={item.href}
                        title={collapsed ? item.name : undefined}
                        onClick={item.href === '/changelog' && hasNewRelease ? onNewReleaseSeen : undefined}
                        className={({ isActive }) =>
                          classNames(
                            isActive
                              ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                              : 'text-gray-700 hover:bg-gray-100/60 dark:text-gray-300 dark:hover:bg-gray-700/50',
                            navItemClass
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon className={classNames(
                              "h-5 w-5 flex-shrink-0 transition-colors",
                              isActive
                                ? "text-brand-600 dark:text-brand-400"
                                : "text-gray-500 group-hover:text-brand-700 dark:text-gray-400 dark:group-hover:text-brand-400"
                            )} />
                            {!collapsed && (
                              <>
                                <span className="truncate flex-1">{item.name}</span>
                                {item.href === '/changelog' && hasNewRelease && (
                                  <span className="ml-auto h-2 w-2 rounded-full bg-brand-500 dark:bg-brand-400 flex-shrink-0" />
                                )}
                              </>
                            )}
                            {collapsed && <span className="sr-only">{item.name}</span>}
                          </>
                        )}
                      </NavLink>
                    )
                  ) : collapsed ? (
                    // Collapsed: icon-only; hover reveals flyout with all children
                    <div
                      onMouseEnter={(e) => openFlyout(e, item)}
                      onMouseLeave={scheduleFlyoutClose}
                    >
                      <NavLink
                        to={item.children[0].href}
                        title={item.name}
                        className={classNames(
                          isActiveParent || isAnySubItemActive || flyout?.item?.name === item.name
                            ? 'text-brand-900 dark:text-brand-300 bg-brand-50/70 dark:bg-brand-900/20'
                            : 'text-gray-700 hover:bg-gray-100/60 dark:text-gray-300 dark:hover:bg-gray-700/50',
                          navItemClass
                        )}
                      >
                        <item.icon className={classNames(
                          "h-5 w-5 flex-shrink-0 transition-colors",
                          isActiveParent || isAnySubItemActive || flyout?.item?.name === item.name
                            ? "text-brand-700 dark:text-brand-400"
                            : "text-gray-500 group-hover:text-brand-700 dark:text-gray-400 dark:group-hover:text-brand-400"
                        )} />
                        <span className="sr-only">{item.name}</span>
                      </NavLink>
                    </div>
                  ) : (
                    // Expanded: full Disclosure group
                    <Disclosure as="div" className="space-y-0.5" defaultOpen={isActiveParent}>
                      {({ open }) => (
                        <>
                          <DisclosureButton
                            className={classNames(
                              'group flex w-full items-center justify-between gap-x-3 rounded-md px-3 py-3 sm:py-2.5 text-sm font-medium leading-6 tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
                              isActiveParent || (open && isAnySubItemActive)
                                ? 'text-brand-900 dark:text-brand-300'
                                : 'text-gray-700 hover:bg-gray-100/60 dark:text-gray-300 dark:hover:bg-gray-700/50'
                            )}
                          >
                            <div className="flex items-center gap-x-3 min-w-0 flex-1">
                              <item.icon className={classNames(
                                "h-5 w-5 flex-shrink-0 transition-colors",
                                isActiveParent || (open && isAnySubItemActive)
                                  ? "text-brand-700 dark:text-brand-400"
                                  : "text-gray-500 group-hover:text-brand-700 dark:text-gray-400 dark:group-hover:text-brand-400"
                              )} />
                              <span className="truncate">{item.name}</span>
                            </div>
                            <ChevronRightIcon
                              className={classNames(
                                open ? 'rotate-90 text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-400',
                                'ml-4 h-4 w-4 shrink-0 transform transition-transform'
                              )}
                              aria-hidden="true"
                            />
                          </DisclosureButton>
                          <DisclosurePanel as="ul" className="mt-1 space-y-0.5 overflow-hidden transition-all duration-200 ease-in-out ml-3 pl-3 border-l-2 border-gray-200/70 dark:border-gray-600/70">
                            {item.children.map((subItem) => (
                              <li key={subItem.name}>
                                {subItem.external ? (
                                  <a
                                    href={subItem.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={classNames(
                                      'hover:text-brand-800 dark:hover:text-brand-400 hover:bg-gray-100/40 dark:hover:bg-gray-700/30 text-gray-700 dark:text-gray-300',
                                      'block rounded-md py-2.5 sm:py-2 pl-5 pr-3 text-xs sm:text-xs leading-5 tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 truncate'
                                    )}
                                  >
                                    {subItem.name}
                                  </a>
                                ) : (
                                  <NavLink
                                    to={subItem.href}
                                    end
                                    className={({ isActive }) =>
                                      classNames(
                                        isActive
                                          ? 'text-brand-900 dark:text-brand-300 font-medium bg-brand-50/50 dark:bg-brand-900/10'
                                          : 'hover:text-brand-800 dark:hover:text-brand-400 hover:bg-gray-100/40 dark:hover:bg-gray-700/30 text-gray-700 dark:text-gray-300',
                                        'block rounded-md py-2.5 sm:py-2 pl-5 pr-3 text-xs sm:text-xs leading-5 tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 truncate'
                                      )
                                    }
                                  >
                                    {subItem.name}
                                  </NavLink>
                                )}
                              </li>
                            ))}
                          </DisclosurePanel>
                        </>
                      )}
                    </Disclosure>
                  )}
                </li>
              );
            })}
          </ul>
        </li>

        {/* Bottom Section - Settings and Logout */}
        <li className="mt-auto space-y-0.5">
          <NavLink
            to="/settings"
            title={collapsed ? 'Settings' : undefined}
            className={({ isActive }) =>
              classNames(
                isActive
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                  : 'text-gray-700 hover:bg-gray-100/60 dark:text-gray-300 dark:hover:bg-gray-700/50',
                collapsed
                  ? 'group flex w-full items-center justify-center rounded-md px-0 py-3 sm:py-2.5 text-sm font-medium leading-6 tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'
                  : 'group flex gap-x-3 rounded-md px-3 py-3 sm:py-2.5 text-sm font-medium leading-6 tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900'
              )
            }
          >
            {({ isActive }) => (
              <>
                <Cog6ToothIcon className={classNames(
                  "h-5 w-5 flex-shrink-0 transition-colors",
                  isActive
                    ? "text-brand-600 dark:text-brand-400"
                    : "text-gray-500 group-hover:text-brand-700 dark:text-gray-400 dark:group-hover:text-brand-400"
                )} aria-hidden="true" />
                {!collapsed && <span className="truncate">Settings</span>}
                {collapsed && <span className="sr-only">Settings</span>}
              </>
            )}
          </NavLink>

          <button
            onClick={handleLogoutClick}
            title={collapsed ? 'Sign Out' : undefined}
            className={classNames(
              'text-gray-700 hover:bg-gray-100/60 dark:text-gray-300 dark:hover:bg-gray-700/50 group rounded-md text-sm font-medium leading-6 tracking-tight w-full text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
              collapsed
                ? 'flex items-center justify-center px-0 py-3 sm:py-2.5'
                : 'flex gap-x-3 px-3 py-3 sm:py-2.5'
            )}
          >
            <PowerIcon className="h-5 w-5 flex-shrink-0 text-gray-500 group-hover:text-brand-700 dark:text-gray-400 dark:group-hover:text-brand-400 transition-colors" aria-hidden="true" />
            {!collapsed && <span className="truncate">Sign Out</span>}
            {collapsed && <span className="sr-only">Sign Out</span>}
          </button>
        </li>
      </ul>

      {/* Collapsed flyout sub-menu — rendered via portal, position-clamped */}
      {collapsed && flyout && (
        <FlyoutMenu
          triggerEl={flyout.triggerEl}
          item={flyout.item}
          onMouseEnter={cancelFlyoutClose}
          onMouseLeave={scheduleFlyoutClose}
          onClose={() => setFlyout(null)}
        />
      )}

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        open={logoutConfirmOpen}
        title="Sign Out"
        description="Are you sure you want to sign out?"
        confirmText="Sign Out"
        cancelText="Cancel"
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
        iconBg="bg-red-100"
        iconColor="text-red-600"
      />
    </nav>
  );
}
